import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Flame, TrendingUp } from 'lucide-react'
import { api } from '@/api/client'
import ErrorBanner from '@/components/ErrorBanner'
import { cn, formatTime, relativeTime } from '@/lib/utils'
import {
  Badge,
  Card,
  EmptyState,
  Select,
  Table,
} from '@/admin-design/_common'
import type {
  AnalysisKeyword,
  AnalysisEdge,
  AnalysisSeries,
  AnalysisTimelineResponse,
} from '@/api/types'

// Match Topics.tsx's compact K/M formatter for scores.
function formatScore(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
  return n.toFixed(0)
}

const WINDOW_OPTIONS = [
  { label: '1일', value: '1' },
  { label: '2일', value: '2' },
  { label: '7일', value: '7' },
  { label: '14일', value: '14' },
  { label: '30일', value: '30' },
]

type Bucket = 'auto' | 'hour' | 'day'

const BUCKET_OPTIONS = [
  { label: '자동', value: 'auto' },
  { label: '시간별', value: 'hour' },
  { label: '일별', value: 'day' },
]

// ─── Inline SVG chart helpers (no chart lib) ─────────────────────────────
// All charts stroke/fill with `currentColor` so an ancestor `text-*` token
// drives the accent in both light and dark mode. Wide charts stretch with
// preserveAspectRatio="none" + non-scaling stroke; sparklines use a fixed
// small viewBox. Every helper guards empty / single-point inputs (no NaN
// paths). Baseline is 0 so magnitudes read honestly.

function areaGeometry(
  values: number[],
  w: number,
  h: number,
  padY = 4,
): { line: string; area: string } | null {
  const n = values.length
  if (n === 0) return null
  const max = Math.max(0, ...values)
  const usable = h - padY * 2
  const yAt = (v: number) => (max <= 0 ? h - padY : padY + (1 - v / max) * usable)
  let pts: Array<[number, number]>
  if (n === 1) {
    // Single bucket → neutral flat line across the middle.
    const y = h / 2
    pts = [[0, y], [w, y]]
  } else {
    pts = values.map((v, i) => [(i / (n - 1)) * w, yAt(v)])
  }
  const line = pts
    .map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(2)},${y.toFixed(2)}`)
    .join(' ')
  const x0 = pts[0][0]
  const xN = pts[pts.length - 1][0]
  const area = `${line} L${xN.toFixed(2)},${h.toFixed(2)} L${x0.toFixed(2)},${h.toFixed(2)} Z`
  return { line, area }
}

/** Wide, full-width area+line chart. Wrap in a `text-*` accent element. */
function AreaChart({ values, height = 150 }: { values: number[]; height?: number }) {
  const W = 600
  const H = height
  const geo = areaGeometry(values, W, H, 6)
  if (!geo) return null
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      className="w-full"
      style={{ height }}
      role="img"
      aria-label="활동량 추이 차트"
    >
      <path d={geo.area} fill="currentColor" fillOpacity={0.12} />
      <path
        d={geo.line}
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        vectorEffect="non-scaling-stroke"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  )
}

/** Compact mini area line for small-multiples. */
function Sparkline({ values }: { values: number[] }) {
  const W = 120
  const H = 34
  const geo = areaGeometry(values, W, H, 3)
  if (!geo) return <div className="h-[34px]" aria-hidden="true" />
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      className="h-[34px] w-full"
      aria-hidden="true"
    >
      <path d={geo.area} fill="currentColor" fillOpacity={0.14} />
      <path
        d={geo.line}
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
        vectorEffect="non-scaling-stroke"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  )
}

/** Evenly spaced indices for axis labels across `n` buckets. */
function pickLabelIndices(n: number, count: number): number[] {
  if (n <= 0) return []
  if (n <= count) return Array.from({ length: n }, (_, i) => i)
  const step = (n - 1) / (count - 1)
  const out: number[] = []
  for (let i = 0; i < count; i++) out.push(Math.round(i * step))
  return [...new Set(out)]
}

function formatBucketLabel(unix: number, bucket: string): string {
  const d = new Date(unix * 1000)
  if (bucket === 'hour') {
    return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}시`
  }
  return `${d.getMonth() + 1}/${d.getDate()}`
}

function formatHourLabel(hour: number): string {
  return `${String(hour).padStart(2, '0')}시`
}

function formatCount(n: number): string {
  return n.toLocaleString('ko-KR')
}

function formatTrendPercent(pct: number): string {
  const abs = Math.abs(pct)
  const rounded = abs >= 100 ? abs.toFixed(0) : abs.toFixed(1).replace(/\.0$/, '')
  return `${rounded}%`
}

type TrendMeta = {
  symbol: string
  cls: string
  label: string
  title: string
  earlier: number
  recent: number
  delta: number
}

function trendMeta(points: number[]): TrendMeta {
  const n = points.length
  if (n < 2) {
    return {
      symbol: '–',
      cls: 'text-muted-foreground',
      label: '±0%',
      title: '비교할 구간이 부족합니다.',
      earlier: 0,
      recent: points[0] ?? 0,
      delta: 0,
    }
  }

  const half = Math.floor(n / 2)
  let earlier = 0
  let recent = 0
  for (let i = 0; i < n; i++) {
    if (i < half) earlier += points[i]
    else recent += points[i]
  }

  const delta = recent - earlier
  const title = `이전 구간 ${formatCount(earlier)}건 · 최근 구간 ${formatCount(recent)}건`
  if (delta === 0) {
    return { symbol: '–', cls: 'text-muted-foreground', label: '±0%', title, earlier, recent, delta }
  }

  if (delta > 0) {
    if (earlier === 0) {
      return {
        symbol: '▲',
        cls: 'text-success',
        label: `신규 +${formatCount(recent)}`,
        title,
        earlier,
        recent,
        delta,
      }
    }
    const pct = (delta / earlier) * 100
    const label = earlier < 5 || pct >= 1000
      ? `+${formatCount(delta)}건`
      : `+${formatTrendPercent(pct)}`
    return { symbol: '▲', cls: 'text-success', label, title, earlier, recent, delta }
  }

  const pct = earlier > 0 ? (Math.abs(delta) / earlier) * 100 : 0
  const label = earlier < 5 ? `−${formatCount(Math.abs(delta))}건` : `−${formatTrendPercent(pct)}`
  return { symbol: '▼', cls: 'text-destructive', label, title, earlier, recent, delta }
}

function MomentumTag({ points }: { points: number[] }) {
  const meta = trendMeta(points)
  return (
    <span
      className={cn('inline-flex shrink-0 items-center gap-0.5 text-[11px] font-medium tabular-nums', meta.cls)}
      title={meta.title}
    >
      <span aria-hidden="true">{meta.symbol}</span>
      {meta.label}
    </span>
  )
}

// ─── 전체 활동량 추이 (volume trend) ──────────────────────────────────────

function StatInline({ label, value }: { label: string; value: number }) {
  return (
    <span>
      {label}{' '}
      <span className="font-medium tabular-nums text-foreground">
        {value.toLocaleString('ko-KR')}
      </span>
    </span>
  )
}

function VolumeTrend({ tl }: { tl: AnalysisTimelineResponse }) {
  const { volume, buckets, bucket, total_items } = tl
  const hasData = volume.length > 0
  const vMax = hasData ? Math.max(...volume) : 0
  const vMin = hasData ? Math.min(...volume) : 0
  const vLast = hasData ? volume[volume.length - 1] : 0
  const labelIdx = pickLabelIndices(buckets.length, 5)
  return (
    <Card className="space-y-2 p-3">
      <div className="text-info">
        {hasData ? (
          <AreaChart values={volume} />
        ) : (
          <div className="flex h-[150px] items-center justify-center text-xs text-muted-foreground">
            표시할 활동량이 없습니다.
          </div>
        )}
      </div>
      {buckets.length > 0 && (
        <div className="flex justify-between text-[10px] tabular-nums text-muted-foreground">
          {labelIdx.map((i) => (
            <span key={i}>{formatBucketLabel(buckets[i], bucket)}</span>
          ))}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-border pt-2 text-xs text-muted-foreground">
        <StatInline label="최근" value={vLast} />
        <span aria-hidden="true">·</span>
        <StatInline label="최대" value={vMax} />
        <span aria-hidden="true">·</span>
        <StatInline label="최소" value={vMin} />
        <span aria-hidden="true">·</span>
        <StatInline label="합계" value={total_items} />
      </div>
    </Card>
  )
}

// ─── 키워드별 추이 (keyword trends, small multiples) ───────────────────────

function KeywordTrends({ series }: { series: AnalysisSeries[] }) {
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
      {series.map((s) => (
        <Card key={s.keyword} className="space-y-1.5 p-2.5">
          <div className="flex items-center justify-between gap-1.5">
            <span className="truncate text-xs font-medium text-foreground" title={s.keyword}>
              {s.keyword}
            </span>
            <MomentumTag points={s.points} />
          </div>
          <div className="text-sm font-semibold tabular-nums text-foreground">
            {s.total.toLocaleString('ko-KR')}
          </div>
          <div className="text-info">
            <Sparkline values={s.points} />
          </div>
        </Card>
      ))}
    </div>
  )
}

// ─── 급상승 키워드 (rising) ────────────────────────────────────────────────

function RisingList({ series }: { series: AnalysisSeries[] }) {
  return (
    <Card className="divide-y divide-border overflow-hidden">
      {series.map((s, i) => {
        const meta = trendMeta(s.points)
        return (
          <div key={s.keyword} className="flex items-center gap-3 px-3 py-2">
            <span className="w-6 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
              {i + 1}
            </span>
            <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground" title={s.keyword}>
              {s.keyword}
            </span>
            <span className="shrink-0 text-xs tabular-nums text-muted-foreground" title={`전체 ${s.total}`}>
              {s.total.toLocaleString('ko-KR')}
            </span>
            <span
              className={cn(
                'inline-flex w-24 shrink-0 items-center justify-end gap-0.5 text-sm font-medium tabular-nums',
                meta.cls,
              )}
              title={meta.title}
            >
              <span aria-hidden="true">{meta.symbol}</span>
              {meta.label}
            </span>
          </div>
        )
      })}
    </Card>
  )
}

// ─── 시간대별 추이 (hour-of-day profile) ─────────────────────────────────

function buildHourProfile(tl: AnalysisTimelineResponse) {
  const volume = Array.from({ length: 24 }, () => 0)
  tl.buckets.forEach((unix, i) => {
    const hour = new Date(unix * 1000).getHours()
    volume[hour] += tl.volume[i] ?? 0
  })

  const series = tl.series
    .map((s) => {
      const points = Array.from({ length: 24 }, () => 0)
      s.points.forEach((v, i) => {
        const unix = tl.buckets[i]
        if (!unix) return
        const hour = new Date(unix * 1000).getHours()
        points[hour] += v
      })
      const total = points.reduce((sum, v) => sum + v, 0)
      return { keyword: s.keyword, total, points }
    })
    .filter((s) => s.total > 0)
    .slice(0, 6)

  let peakHour = 0
  for (let hour = 1; hour < 24; hour++) {
    if (volume[hour] > volume[peakHour]) peakHour = hour
  }

  return { volume, series, peakHour, peakValue: volume[peakHour] ?? 0 }
}

function HourBars({ values }: { values: number[] }) {
  const max = Math.max(1, ...values)
  return (
    <div className="overflow-x-auto">
      <div className="min-w-[560px]">
        <div
          className="grid h-28 items-end gap-1"
          style={{ gridTemplateColumns: 'repeat(24, minmax(10px, 1fr))' }}
        >
          {values.map((v, hour) => {
            const height = v <= 0 ? 2 : Math.max(6, (v / max) * 104)
            const opacity = v <= 0 ? 0.18 : 0.35 + 0.65 * (v / max)
            return (
              <div key={hour} className="flex h-28 items-end" title={`${formatHourLabel(hour)} · ${formatCount(v)}건`}>
                <div
                  className="w-full rounded-t-sm bg-info"
                  style={{ height, opacity }}
                />
              </div>
            )
          })}
        </div>
        <div
          className="mt-1 grid gap-1 text-center text-[10px] tabular-nums text-muted-foreground"
          style={{ gridTemplateColumns: 'repeat(24, minmax(10px, 1fr))' }}
        >
          {values.map((_, hour) => (
            <span key={hour}>{hour % 6 === 0 || hour === 23 ? String(hour).padStart(2, '0') : ''}</span>
          ))}
        </div>
      </div>
    </div>
  )
}

function HourKeywordRows({ series }: { series: Array<{ keyword: string; total: number; points: number[] }> }) {
  if (series.length === 0) return null
  return (
    <div className="overflow-x-auto border-t border-border pt-3">
      <div className="min-w-[640px] space-y-1.5">
        {series.map((s) => {
          const rowMax = Math.max(1, ...s.points)
          return (
            <div key={s.keyword} className="flex items-center gap-2">
              <div className="flex w-28 shrink-0 items-baseline gap-1 sm:w-36">
                <span className="min-w-0 flex-1 truncate text-xs font-medium text-foreground" title={s.keyword}>
                  {s.keyword}
                </span>
                <span className="text-[10px] tabular-nums text-muted-foreground">
                  {formatCount(s.total)}
                </span>
              </div>
              <div
                className="grid flex-1 gap-0.5"
                style={{ gridTemplateColumns: 'repeat(24, minmax(8px, 1fr))' }}
              >
                {s.points.map((v, hour) => {
                  const alpha = v <= 0 ? 0.04 : 0.14 + 0.86 * (v / rowMax)
                  return (
                    <div
                      key={hour}
                      className="h-5 rounded-sm"
                      style={{ backgroundColor: `rgb(var(--info) / ${alpha.toFixed(3)})` }}
                      title={`${s.keyword} · ${formatHourLabel(hour)}: ${formatCount(v)}건`}
                    />
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function HourOfDayTrend({ tl }: { tl: AnalysisTimelineResponse }) {
  const profile = useMemo(() => buildHourProfile(tl), [tl])
  const hasVolume = profile.volume.some((v) => v > 0)
  return (
    <Card className="space-y-3 p-3">
      {hasVolume ? (
        <>
          <HourBars values={profile.volume} />
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            <span>
              피크{' '}
              <span className="font-medium tabular-nums text-foreground">
                {formatHourLabel(profile.peakHour)}
              </span>
            </span>
            <span aria-hidden="true">·</span>
            <StatInline label="피크 활동량" value={profile.peakValue} />
          </div>
          <HourKeywordRows series={profile.series} />
        </>
      ) : (
        <div className="flex h-[120px] items-center justify-center text-xs text-muted-foreground">
          시간대별로 표시할 활동량이 없습니다.
        </div>
      )}
    </Card>
  )
}

// ─── 키워드 활동 히트맵 (heatmap) ─────────────────────────────────────────

function Heatmap({
  series,
  buckets,
  bucket,
}: {
  series: AnalysisSeries[]
  buckets: number[]
  bucket: string
}) {
  const labelIdx = pickLabelIndices(buckets.length, Math.min(6, buckets.length))
  return (
    <Card className="overflow-hidden">
      <div className="overflow-x-auto">
        <div className="min-w-[480px] p-3">
          {/* Column header — a few bucket labels spread across the columns */}
          <div className="mb-1 flex items-center gap-1">
            <div className="w-24 shrink-0 sm:w-32" />
            <div className="flex flex-1 justify-between text-[10px] tabular-nums text-muted-foreground">
              {labelIdx.map((i) => (
                <span key={i}>{formatBucketLabel(buckets[i], bucket)}</span>
              ))}
            </div>
          </div>
          {series.map((s) => {
            const rowMax = Math.max(1, ...s.points)
            return (
              <div key={s.keyword} className="flex items-center gap-1 py-0.5">
                <span
                  className="w-24 shrink-0 truncate text-xs text-foreground sm:w-32"
                  title={s.keyword}
                >
                  {s.keyword}
                </span>
                <div className="flex flex-1 gap-0.5">
                  {s.points.map((v, i) => {
                    const alpha = v <= 0 ? 0.04 : 0.12 + 0.88 * (v / rowMax)
                    return (
                      <div
                        key={i}
                        className="h-5 min-w-[8px] flex-1 rounded-sm"
                        style={{ backgroundColor: `rgb(var(--info) / ${alpha.toFixed(3)})` }}
                        title={`${s.keyword} · ${formatBucketLabel(buckets[i], bucket)}: ${v}`}
                      />
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </Card>
  )
}

// ─── Keyword ranking (horizontal bars) ──────────────────────────────────

function KeywordRanking({ keywords }: { keywords: AnalysisKeyword[] }) {
  const maxTopicCount = Math.max(1, ...keywords.map((k) => k.topic_count))
  return (
    <Card className="divide-y divide-border overflow-hidden">
      {keywords.map((kw, i) => (
        <div key={kw.keyword} className="flex items-center gap-3 px-3 py-2">
          <span className="w-6 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
            {i + 1}
          </span>
          <div className="flex w-28 shrink-0 items-center gap-1.5 sm:w-40">
            {kw.spikes > 0 && (
              <Flame size={13} className="shrink-0 text-destructive" aria-label={`급상승 ${kw.spikes}회`} />
            )}
            <span className="truncate text-sm font-medium text-foreground" title={kw.keyword}>
              {kw.keyword}
            </span>
          </div>
          <div className="min-w-0 flex-1">
            <div className="h-2 overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-foreground/70"
                style={{ width: `${(kw.topic_count / maxTopicCount) * 100}%` }}
              />
            </div>
          </div>
          <span className="w-8 shrink-0 text-right text-sm font-medium tabular-nums text-foreground">
            {kw.topic_count}
          </span>
          <span
            className="hidden w-12 shrink-0 text-right text-[11px] tabular-nums text-muted-foreground sm:inline"
            title={`score ${kw.score.toFixed(1)}`}
          >
            {formatScore(kw.score)}
          </span>
        </div>
      ))}
    </Card>
  )
}

// ─── Co-occurrence table ─────────────────────────────────────────────────

function EdgeTable({ edges }: { edges: AnalysisEdge[] }) {
  const maxWeight = Math.max(1, ...edges.map((e) => e.weight))
  return (
    <Card className="overflow-hidden">
      <Table>
        <Table.Head>
          <Table.Row interactive={false}>
            <Table.Header>키워드 A</Table.Header>
            <Table.Header>키워드 B</Table.Header>
            <Table.Header className="text-right">동시 출현</Table.Header>
          </Table.Row>
        </Table.Head>
        <Table.Body>
          {edges.length === 0 ? (
            <Table.Empty colSpan={3} title="연관 키워드가 없습니다." />
          ) : (
            edges.map((e, i) => (
              <Table.Row key={`${e.source}-${e.target}-${i}`} interactive={false}>
                <Table.Cell>
                  <Badge shape="square" className="font-medium">{e.source}</Badge>
                </Table.Cell>
                <Table.Cell>
                  <Badge shape="square" className="font-medium">{e.target}</Badge>
                </Table.Cell>
                <Table.Cell className="text-right">
                  <div className="flex items-center justify-end gap-2">
                    <div className="hidden h-1.5 w-24 overflow-hidden rounded-full bg-muted sm:block">
                      <div
                        className="h-full rounded-full bg-info"
                        style={{ width: `${(e.weight / maxWeight) * 100}%` }}
                      />
                    </div>
                    <span className="w-6 text-right font-medium tabular-nums text-foreground">
                      {e.weight}
                    </span>
                  </div>
                </Table.Cell>
              </Table.Row>
            ))
          )}
        </Table.Body>
      </Table>
    </Card>
  )
}

// ─── Section heading ─────────────────────────────────────────────────────

function SectionHeading({ children, hint }: { children: React.ReactNode; hint?: string }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
      <h2 className="flex items-center gap-1.5 text-sm font-semibold text-foreground">{children}</h2>
      {hint && <span className="text-[11px] text-muted-foreground">{hint}</span>}
    </div>
  )
}

// ─── Page ────────────────────────────────────────────────────────────────

export default function Analysis() {
  const [windowDays, setWindowDays] = useState(7)
  const [bucket, setBucket] = useState<Bucket>('auto')
  const mainTimelineIsHourly = bucket === 'hour' || (bucket === 'auto' && windowDays <= 2)

  const kwQuery = useQuery({
    queryKey: ['analysis-keywords', windowDays],
    queryFn: () => api.getAnalysisKeywords({ window_days: windowDays }),
    refetchInterval: 60_000,
  })

  const tlQuery = useQuery({
    queryKey: ['analysis-timeline', windowDays, bucket],
    queryFn: () => api.getAnalysisTimeline({ window_days: windowDays, bucket }),
    refetchInterval: 60_000,
  })

  const hourlyQuery = useQuery({
    queryKey: ['analysis-timeline-hour-profile', windowDays],
    queryFn: () => api.getAnalysisTimeline({ window_days: windowDays, bucket: 'hour', limit: 6 }),
    enabled: !mainTimelineIsHourly,
    refetchInterval: 60_000,
  })

  const kwData = kwQuery.data
  const tlData = tlQuery.data
  const hourlyData = mainTimelineIsHourly && tlData?.bucket === 'hour' ? tlData : hourlyQuery.data

  const isLoading = kwQuery.isLoading || tlQuery.isLoading
  const isFetching = kwQuery.isFetching || tlQuery.isFetching || hourlyQuery.isFetching

  const topicCount = kwData?.topic_count ?? 0
  const totalItems = tlData?.total_items ?? 0
  const bothEmpty = !!kwData && !!tlData && topicCount === 0 && totalItems === 0
  const generatedAt = tlData?.generated_at ?? kwData?.generated_at

  const risingList = useMemo(() => {
    if (!tlData) return []
    return [...tlData.series]
      .filter((s) => trendMeta(s.points).delta > 0)
      .sort((a, b) => {
        const ta = trendMeta(a.points)
        const tb = trendMeta(b.points)
        if (tb.delta !== ta.delta) return tb.delta - ta.delta
        if (tb.recent !== ta.recent) return tb.recent - ta.recent
        return b.total - a.total
      })
      .slice(0, 6)
  }, [tlData])

  const hasSeries = !!tlData && tlData.series.length > 0

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-xl font-semibold">키워드 분석</h1>
        <div className="flex items-center gap-2">
          {isFetching && (
            <span className="text-xs text-muted-foreground animate-pulse">syncing...</span>
          )}
          <Select
            value={String(windowDays)}
            onValueChange={(v) => setWindowDays(Number(v))}
            options={WINDOW_OPTIONS}
            className="max-w-[90px]"
            aria-label="분석 기간"
          />
          <Select
            value={bucket}
            onValueChange={(v) => setBucket(v as Bucket)}
            options={BUCKET_OPTIONS}
            className="max-w-[100px]"
            aria-label="시간 단위"
          />
        </div>
      </div>

      {(kwData || tlData) && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          <span>
            분석 토픽 수{' '}
            <span className="font-medium tabular-nums text-foreground">
              {topicCount.toLocaleString('ko-KR')}
            </span>
          </span>
          <span aria-hidden="true">·</span>
          <span>
            전체 아이템 수{' '}
            <span className="font-medium tabular-nums text-foreground">
              {totalItems.toLocaleString('ko-KR')}
            </span>
          </span>
          {generatedAt ? (
            <>
              <span aria-hidden="true">·</span>
              <span title={formatTime(generatedAt)}>갱신 {relativeTime(generatedAt)}</span>
            </>
          ) : null}
        </div>
      )}

      {kwQuery.error && <ErrorBanner error={kwQuery.error} />}
      {tlQuery.error && <ErrorBanner error={tlQuery.error} />}
      {hourlyQuery.error && <ErrorBanner error={hourlyQuery.error} />}

      {isLoading ? (
        <div className="space-y-4">
          <Card className="p-3">
            <div className="h-[150px] w-full animate-pulse rounded bg-muted" />
          </Card>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
            {[1, 2, 3, 4].map((i) => (
              <Card key={i} className="p-2.5">
                <div className="h-16 w-full animate-pulse rounded bg-muted" />
              </Card>
            ))}
          </div>
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="p-3">
                <div className="h-4 w-2/3 animate-pulse rounded bg-muted" />
              </Card>
            ))}
          </div>
        </div>
      ) : bothEmpty ? (
        <Card>
          <EmptyState
            title="토픽이 아직 없습니다"
            description="소스 수집과 클러스터링·라벨링이 실행되면 키워드와 활동 추이가 집계됩니다."
          />
        </Card>
      ) : (
        <div className="space-y-6">
          {/* 전체 활동량 추이 — works even before topics are labelled */}
          {tlData && (
            <section className="space-y-2">
              <SectionHeading hint={`시간 단위: ${tlData.bucket === 'hour' ? '시간별' : '일별'}`}>
                <TrendingUp size={14} /> 전체 활동량 추이
              </SectionHeading>
              <VolumeTrend tl={tlData} />
            </section>
          )}

          {hourlyData && hourlyData.total_items > 0 && (
            <section className="space-y-2">
              <SectionHeading hint={`최근 ${windowDays}일 · 00시-23시 누적`}>
                시간대별 추이
              </SectionHeading>
              <HourOfDayTrend tl={hourlyData} />
            </section>
          )}

          {/* Items collected but no topics yet → keyword sections pending */}
          {totalItems > 0 && topicCount === 0 && (
            <Card>
              <EmptyState
                title="키워드 집계 대기 중"
                description="아이템은 수집됐지만 아직 토픽 클러스터링·라벨링 전입니다. 클러스터링이 실행되면 키워드별 추이·급상승·히트맵·랭킹이 채워집니다."
              />
            </Card>
          )}

          {/* 키워드별 추이 (small multiples) */}
          {hasSeries && (
            <section className="space-y-2">
              <SectionHeading hint="상위 키워드별 구간 추이">키워드별 추이</SectionHeading>
              <KeywordTrends series={tlData!.series} />
            </section>
          )}

          {/* 급상승 키워드 */}
          {hasSeries && (
            <section className="space-y-2">
              <SectionHeading hint="모멘텀 상위">
                <Flame size={14} className="text-destructive" /> 급상승 키워드
              </SectionHeading>
              {risingList.length === 0 ? (
                <Card>
                  <EmptyState title="급상승 중인 키워드가 없습니다." />
                </Card>
              ) : (
                <RisingList series={risingList} />
              )}
            </section>
          )}

          {/* 키워드 활동 히트맵 */}
          {hasSeries && tlData!.buckets.length > 0 && (
            <section className="space-y-2">
              <SectionHeading hint="행=키워드 · 열=구간 · 진할수록 활발">키워드 활동 히트맵</SectionHeading>
              <Heatmap series={tlData!.series} buckets={tlData!.buckets} bucket={tlData!.bucket} />
            </section>
          )}

          {/* 키워드 랭킹 (existing) */}
          {kwData && kwData.keywords.length > 0 && (
            <section className="space-y-2">
              <SectionHeading>키워드 랭킹</SectionHeading>
              <KeywordRanking keywords={kwData.keywords} />
            </section>
          )}

          {/* 연관 키워드 (existing) */}
          {kwData && (
            <section className="space-y-2">
              <SectionHeading>연관 키워드</SectionHeading>
              <EdgeTable edges={kwData.edges} />
            </section>
          )}
        </div>
      )}
    </div>
  )
}
